import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { shipmentsApi } from '../api/shipments-api';
import { ConflictError } from '../api/errors';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import {
  directionLabel,
  formatShipmentAddress,
  shipmentStateLabels,
  type ConsumableUnit,
  type ShipmentPatch,
} from '../domain/shipment';
import { CancelShipmentAction } from '../features/shipments/CancelShipmentAction';
import { ShipmentLabelPanel } from '../features/shipments/ShipmentLabelPanel';
import { ShipmentTrackingPanel } from '../features/shipments/ShipmentTrackingPanel';
import {
  INSTALLATION_LAG_THRESHOLD_DAYS,
  daysSince,
  isInstallationOverdue,
} from '../features/shipments/installation-lag';
import { SerialSourceBadge } from '../features/shipments/SerialSourceBadge';

const CONSUMABLE_UNITS: ConsumableUnit[] = ['METERS', 'PIECES', 'KILOGRAMS'];

type ConsumableDraft = { description: string; quantity: string; unit: ConsumableUnit };

const emptyConsumableDraft: ConsumableDraft = { description: '', quantity: '', unit: 'PIECES' };

export function ShipmentDetailPage() {
  const { shipmentId = '' } = useParams();
  const queryClient = useQueryClient();
  const shipment = useQuery({
    queryKey: ['shipment', shipmentId],
    queryFn: () => shipmentsApi.getShipment(shipmentId),
  });
  const [draftConsumables, setDraftConsumables] = useState<Record<string, ConsumableDraft>>({});
  const [draftUnassignedSerial, setDraftUnassignedSerial] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // UpdateShipment merges only the fields the patch populates — pallets and
  // unassignedSerials are replaced wholesale when included, left untouched
  // when omitted — so each mutation below only needs to send the one list
  // it's actually changing, always in full (not just the added entry).
  const mutation = useMutation({
    mutationFn: (patch: ShipmentPatch) => shipmentsApi.updateShipment(patch),
    onSuccess: (updated) => {
      queryClient.setQueryData(['shipment', shipmentId], updated);
      setFormError(null);
    },
    onError: (error: unknown) => {
      if (error instanceof ConflictError) {
        setFormError('This shipment changed since it was loaded. Reload before adding more.');
        return;
      }
      setFormError(error instanceof Error ? error.message : 'Failed to update shipment.');
    },
  });

  if (shipment.isLoading) return <PageLoadingState label="Loading shipment…" />;
  if (shipment.isError) {
    return (
      <ErrorState
        title="Shipment could not be loaded"
        rpcName="GetShipment"
        onRetry={() => void shipment.refetch()}
      />
    );
  }

  const data = shipment.data;
  if (!data) return null;

  const allSerials = [...data.pallets.flatMap((pallet) => pallet.serials), ...data.unassignedSerials];
  const confirmedSerialCount = allSerials.filter((serial) => serial.source === 'FIELD_ENTRY').length;
  const deliveredDaysAgo = data.delivery ? daysSince(data.delivery.deliveredAt) : null;
  const overdue = data.delivery
    ? isInstallationOverdue(data.delivery.deliveredAt, confirmedSerialCount)
    : false;

  function addUnassignedSerial(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!data) return;
    const value = draftUnassignedSerial.trim();
    if (!value) return;
    const unassignedSerials = [
      ...data.unassignedSerials,
      { itemId: value, source: 'FIELD_ENTRY' as const },
    ];
    mutation.mutate({ id: data.id, version: data.version, unassignedSerials });
    setDraftUnassignedSerial('');
  }

  function addConsumable(palletId: string, event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!data) return;
    const draft = draftConsumables[palletId] ?? emptyConsumableDraft;
    const description = draft.description.trim();
    const quantity = Number(draft.quantity);
    if (!description || !Number.isFinite(quantity) || quantity <= 0) return;
    const pallets = data.pallets.map((pallet) =>
      pallet.id === palletId
        ? { ...pallet, consumables: [...pallet.consumables, { description, quantity, unit: draft.unit }] }
        : pallet,
    );
    mutation.mutate({ id: data.id, version: data.version, pallets });
    setDraftConsumables((current) => ({ ...current, [palletId]: emptyConsumableDraft }));
  }

  return (
    <>
      <PageHeader
        backLink={<Link to="/shipments">Shipments</Link>}
        title={
          data.direction === 'RETURN' && data.origin
            ? `${data.origin.name} → ${data.destinationCustomer}`
            : data.destinationCustomer
        }
        description="Pallet tracking backed by Cargonizer (tidaltech/koi#7882)."
        actions={<CancelShipmentAction shipment={data} />}
      />
      {formError ? (
        <p role="alert" className="form-error">
          {formError}
        </p>
      ) : null}

      <dl className="site-details">
        <dt>Direction</dt>
        <dd>
          <span
            className={`direction-badge direction-${data.direction === 'RETURN' ? 'return' : 'outbound'}`}
          >
            {directionLabel(data.direction)}
          </span>
        </dd>
        {data.direction === 'RETURN' && data.origin ? (
          <>
            <dt>Collected from</dt>
            <dd>
              {data.origin.name}
              {data.origin.contactName ? ` (contact: ${data.origin.contactName})` : ''}
              {data.origin.address ? <>, {formatShipmentAddress(data.origin.address)}</> : null}
            </dd>
          </>
        ) : null}
        <dt>Cargonizer consignment</dt>
        <dd className="mono">{data.cargonizerConsignmentNumber}</dd>
        <dt>Carrier</dt>
        <dd>{data.carrier}</dd>
        <dt>{data.direction === 'RETURN' ? 'Delivered to' : 'Destination'}</dt>
        <dd>
          {data.direction === 'RETURN' ? `${data.destinationCustomer}, ` : null}
          {data.destinationAddress.line1}
          {data.destinationAddress.line2 ? `, ${data.destinationAddress.line2}` : ''},{' '}
          {data.destinationAddress.postcode} {data.destinationAddress.city}
          {data.destinationContactName ? ` (contact: ${data.destinationContactName})` : ''}
        </dd>
        <dt>Shipped</dt>
        <dd>{new Date(data.shippedAt).toLocaleString()}</dd>
        {data.reason ? (
          <>
            <dt>Reason</dt>
            <dd>{data.reason}</dd>
          </>
        ) : null}
        {data.senderReference ? (
          <>
            <dt>Rebill freight</dt>
            <dd>{data.senderReference}</dd>
          </>
        ) : null}
        {data.requestedBy ? (
          <>
            <dt>Requested by</dt>
            <dd>{data.requestedBy}</dd>
          </>
        ) : null}
        <dt>Status</dt>
        <dd>
          {data.trackingUrl ? (
            <a
              className="state-badge state-badge-ready"
              href={data.trackingUrl}
              target="_blank"
              rel="noreferrer"
            >
              {shipmentStateLabels[data.state]} · Track
            </a>
          ) : (
            <>
              <span
                className={`state-badge ${data.state === 'CANCELLED' ? 'state-badge-muted' : 'state-badge-ready'}`}
              >
                {shipmentStateLabels[data.state]}
              </span>
              {data.state === 'CANCELLED' ? (
                <p className="empty-note">
                  Never sent, so it isn’t tracked and its units weren’t changed.
                  {data.cancelNote ? ` Note: ${data.cancelNote}` : ''}
                </p>
              ) : null}
              {data.state === 'TRANSFERRED' ? (
                <p className="empty-note">No tracking page available from {data.carrier}.</p>
              ) : null}
            </>
          )}
        </dd>
        {data.delivery ? (
          <>
            <dt>Delivery</dt>
            <dd>
              <span className="state-badge state-badge-ready">{data.delivery.status}</span>
              {overdue ? (
                <span className="attention-badge">
                  Past {INSTALLATION_LAG_THRESHOLD_DAYS}-day installation check
                </span>
              ) : null}
              <p className="empty-note">
                {deliveredDaysAgo === 0
                  ? 'Delivered today'
                  : `Delivered ${deliveredDaysAgo} day${deliveredDaysAgo === 1 ? '' : 's'} ago`}
                {confirmedSerialCount === 0
                  ? overdue
                    ? ' — no installation confirmed. Worth a check-in, not a guess.'
                    : ' — no installation confirmed yet.'
                  : '.'}
              </p>
            </dd>
          </>
        ) : null}
      </dl>

      <ShipmentTrackingPanel shipment={data} />

      <ShipmentLabelPanel shipment={data} />

      <article className="site-card">
        <div className="site-card-heading">
          <div>
            <h2>Contents</h2>
          </div>
        </div>
        <p className="empty-note">
          Serials picked on a fulfillment order are added here when the order's packing is finished
          or linked to this shipment, so there is no need to scan them again or split them onto
          pallets (tidaltech/koi#8294).
        </p>
        <div className="unit-list">
          <strong className="unit-count-label">Serials ({data.unassignedSerials.length})</strong>
          {data.unassignedSerials.length > 0 ? (
            <ul>
              {data.unassignedSerials.map((serial) => (
                <li key={serial.itemId}>
                  <span className="mono">{serial.itemId}</span>
                  <SerialSourceBadge source={serial.source} confidence={serial.confidence} />
                  {serial.note ? <span className="shipment-serial-note">{serial.note}</span> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="empty-note">No serials on this shipment yet.</p>
          )}
        </div>
        <form
          className="workspace-toolbar"
          onSubmit={addUnassignedSerial}
          aria-label="Add a serial to this shipment"
        >
          <label className="search-field">
            <span className="visually-hidden">Serial to add to this shipment</span>
            <input
              type="text"
              placeholder="Scan or type a serial"
              value={draftUnassignedSerial}
              onChange={(event) => setDraftUnassignedSerial(event.target.value)}
              disabled={mutation.isPending}
            />
          </label>
          <button type="submit" disabled={mutation.isPending}>
            Add
          </button>
        </form>
      </article>

      <div className="site-card-grid">
        {data.pallets.map((pallet, index) => {
          const serials = pallet.serials;
          const consumables = pallet.consumables;
          const consumableDraft = draftConsumables[pallet.id] ?? emptyConsumableDraft;
          return (
            <article className="site-card" key={pallet.id}>
              <div className="site-card-heading">
                <div>
                  <h2>
                    Pallet {index + 1} of {data.pallets.length}
                  </h2>
                  <span className="mono">{pallet.cargonizerPieceNumber}</span>
                </div>
              </div>

              <dl className="site-details">
                <dt>Weight</dt>
                <dd>{pallet.weightKg} kg</dd>
                <dt>Dimensions</dt>
                <dd>
                  {pallet.lengthCm} × {pallet.widthCm} × {pallet.heightCm} cm
                </dd>
              </dl>

              {/* Serials recorded on a pallet before #8294 stay visible here;
                  new ones go on the shipment's Contents. */}
              {serials.length > 0 ? (
                <div className="unit-list">
                  <strong className="unit-count-label">Serials ({serials.length})</strong>
                  <ul>
                    {serials.map((serial) => (
                      <li key={serial.itemId}>
                        <span className="mono">{serial.itemId}</span>
                        <SerialSourceBadge source={serial.source} confidence={serial.confidence} />
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="unit-list">
                <strong className="unit-count-label">Consumables ({consumables.length})</strong>
                {consumables.length > 0 ? (
                  <ul>
                    {consumables.map((consumable, consumableIndex) => (
                      <li key={`${consumable.description}-${consumableIndex}`}>
                        <span>{consumable.description}</span>
                        <span className="mono">
                          {consumable.quantity} {consumable.unit}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="empty-note">No consumables recorded for this pallet yet.</p>
                )}
              </div>

              <form
                className="workspace-toolbar"
                onSubmit={(event) => addConsumable(pallet.id, event)}
                aria-label={`Add a consumable to pallet ${index + 1}`}
              >
                <label className="search-field">
                  <span className="visually-hidden">Consumable description</span>
                  <input
                    type="text"
                    placeholder="e.g. Electrical wire, 2.5mm²"
                    value={consumableDraft.description}
                    onChange={(event) =>
                      setDraftConsumables((current) => ({
                        ...current,
                        [pallet.id]: { ...consumableDraft, description: event.target.value },
                      }))
                    }
                  />
                </label>
                <label className="search-field">
                  <span className="visually-hidden">Quantity</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    placeholder="Qty"
                    value={consumableDraft.quantity}
                    onChange={(event) =>
                      setDraftConsumables((current) => ({
                        ...current,
                        [pallet.id]: { ...consumableDraft, quantity: event.target.value },
                      }))
                    }
                  />
                </label>
                <label className="search-field">
                  <span className="visually-hidden">Unit</span>
                  <select
                    value={consumableDraft.unit}
                    onChange={(event) =>
                      setDraftConsumables((current) => ({
                        ...current,
                        [pallet.id]: { ...consumableDraft, unit: event.target.value as ConsumableUnit },
                      }))
                    }
                  >
                    {CONSUMABLE_UNITS.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </label>
                <button type="submit" disabled={mutation.isPending}>
                  Add
                </button>
              </form>
            </article>
          );
        })}
      </div>
    </>
  );
}
